import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmHost } from "@/components/app/ConfirmDialog";

import AppLayout from "@/components/app/AppLayout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";

// Code splitting : chaque page est chargée à la demande (bundle initial allégé)
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const InvoiceDetail = lazy(() => import("@/pages/InvoiceDetail"));
const Services = lazy(() => import("@/pages/Services"));
const Payments = lazy(() => import("@/pages/Payments"));
const Reconciliation = lazy(() => import("@/pages/Reconciliation"));
const Calendly = lazy(() => import("@/pages/Calendly"));
const Imports = lazy(() => import("@/pages/Imports"));
const Exports = lazy(() => import("@/pages/Exports"));
const EReporting = lazy(() => import("@/pages/EReporting"));
const Audit = lazy(() => import("@/pages/Audit"));
const Settings = lazy(() => import("@/pages/Settings"));
const Accounting = lazy(() => import("@/pages/Accounting"));
const Approvals = lazy(() => import("@/pages/Approvals"));
const InternGuide = lazy(() => import("@/pages/InternGuide"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-stone-500">Vérification…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-stone-500">Chargement…</div>}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          element={
            <Protected>
              <AppLayout />
            </Protected>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/patients" element={<Patients />} />
          <Route path="/patients/:id" element={<PatientDetail />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/:id" element={<InvoiceDetail />} />
          <Route path="/services" element={<Services />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/reconciliation" element={<Reconciliation />} />
          <Route path="/calendly" element={<Calendly />} />
          <Route path="/imports" element={<Imports />} />
          <Route path="/exports" element={<Exports />} />
          <Route path="/ereporting" element={<EReporting />} />
          <Route path="/accounting" element={<Accounting />} />
          <Route path="/validations" element={<Approvals />} />
          <Route path="/guide" element={<InternGuide />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
        <ConfirmHost />
      </AuthProvider>
    </BrowserRouter>
  );
}
